import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import lottie, { AnimationItem } from "lottie-web";
import Locale from "../i18n/Locale";
import cbLogoAnimation from "../../images/cb-logo-lottie.json";

const Bar = styled.header`
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: transparent;
  color: ${({ theme }) => theme.header.textColor};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  height: 72px;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 10;
  --header-font-size: 16px;

  @media screen and (min-width: 1280px) {
    --header-font-size: 18px;
  }
`;

const Container = styled.div<{ $isDesktop: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px clamp(16px, 8.9vw, 170px);
  gap: 16px;
  width: 100%;
  max-width: 1920px;
  height: 72px;

  min-width: 0;
  position: relative;

  @media screen and (max-width: 720px) {
    padding: 12px 16px;
  }
`;

const LeftFrame = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
  max-width: 526.67px;
  height: 36px;

  @media screen and (max-width: 720px) {
    display: none;
  }
`;

const LeftLink = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 8px;
  height: 36px;
  background: ${({ theme }) => theme.header.backgroundColor};
  border-radius: 4px;
  text-decoration: none;
  color: #23231f;
  font-family: ${({ theme }) => theme.fontFamily};
  font-style: normal;
  font-weight: 400;
  font-size: var(--header-font-size);
  line-height: 2rem;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-style: dotted;
  }
`;

const CenterFrame = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 8px;
  flex: 1 1 0;
  min-width: 0;
  max-width: 526.67px;
  height: 30.03px;

  @media screen and (max-width: 720px) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    flex: none;
  }
`;

const LanguagePill = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 4px 8px 8px;
  gap: 4px;
  height: 36px;
  background: ${({ theme }) => theme.header.backgroundColor};
  border-radius: 4px;
  color: #23231f;
  position: relative;
`;

const LanguageButton = styled.button`
  width: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  color: inherit;
  font-family: ${({ theme }) => theme.fontFamily};
  font-style: normal;
  font-weight: 400;
  font-size: var(--header-font-size);
  line-height: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  &:focus {
    outline: none;
  }
`;

const Chevron = styled.svg`
  width: 20px;
  height: 20px;
  flex: none;
  color: #23231f;
`;

const LanguageMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: #faf5eb;
  border: 1px solid #e6dfd2;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 6px;
  min-width: 96px;
  z-index: 20;
`;

const LanguageOption = styled.button<{ $active: boolean }>`
  width: 100%;
  border: 0;
  background: transparent;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: var(--header-font-size);
  line-height: 2rem;
  color: #23231f;
  cursor: pointer;

  &:hover {
    background: #eadbbe;
  }
`;

const CheckIcon = styled.span`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SupportLink = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 8px;
  height: 36px;
  background: ${({ theme }) => theme.header.backgroundColor};
  border-radius: 4px;
  text-decoration: none;
  color: #23231f;
  font-family: ${({ theme }) => theme.fontFamily};
  font-style: normal;
  font-weight: 400;
  font-size: var(--header-font-size);
  line-height: 2rem;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-style: dotted;
  }
`;

const RightFrame = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0px;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
  max-width: 526.67px;
  height: 36px;

  @media screen and (max-width: 720px) {
    display: none;
  }
`;

const Logo = styled.div`
  width: 112px;
  height: 30.03px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: 768px) {
    width: 128px;
  }

  @media screen and (min-width: 1280px) {
    width: 144px;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const MobileLeft = styled.div`
  display: none;

  @media screen and (max-width: 720px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const MobileRight = styled.div`
  display: none;

  @media screen and (max-width: 720px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const HelpIconButton = styled.a`
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #23231f;
`;

export interface HeaderProps {
  currentLanguage: string;
  supportedLocales: Array<Locale>;
  setLanguage: (lng: string) => void;
  isDesktop: boolean;
  aboutText: string;
  registerText: string;
  supportText: string;
  aboutUrl: string;
  registerUrl: string;
  supportUrl: string;
}

function LanguageDropdown({
  currentLanguage,
  supportedLocales,
  setLanguage,
  testId,
}: {
  currentLanguage: string;
  supportedLocales: Array<Locale>;
  setLanguage: (lng: string) => void;
  testId: string;
}) {
  const normalizedLang = currentLanguage.split("-")[0] || currentLanguage;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <LanguagePill aria-label="Language selector" ref={menuRef}>
      <LanguageButton
        aria-label="Language"
        data-testid={testId}
        onClick={() => setMenuOpen((v) => !v)}
      >
        {(normalizedLang || currentLanguage).toUpperCase()}
        <Chevron viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"
          />
        </Chevron>
      </LanguageButton>
      {menuOpen && (
        <LanguageMenu>
          {supportedLocales.map((lang: Locale) => (
            <LanguageOption
              key={lang.key}
              $active={lang.key === normalizedLang}
              onClick={() => {
                setLanguage(lang.key);
                setMenuOpen(false);
              }}
            >
              {lang.key.toUpperCase()}
              {lang.key === normalizedLang && (
                <CheckIcon aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12l4 4 10-10"
                      stroke="#55544C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </CheckIcon>
              )}
            </LanguageOption>
          ))}
        </LanguageMenu>
      )}
    </LanguagePill>
  );
}

export default function Header(props: HeaderProps): JSX.Element {
  const logoRef = useRef<HTMLDivElement | null>(null);
  const logoAnimRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!logoRef.current || logoAnimRef.current) {
      return;
    }

    logoAnimRef.current = lottie.loadAnimation({
      container: logoRef.current,
      renderer: "svg",
      loop: false,
      autoplay: false,
      animationData: cbLogoAnimation,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid meet",
      },
    });

    return () => {
      logoAnimRef.current?.destroy();
      logoAnimRef.current = null;
    };
  }, []);

  function playLogoOnce() {
    if (logoAnimRef.current) {
      logoAnimRef.current.setSpeed(1.1);
      logoAnimRef.current.setDirection(1);
      logoAnimRef.current.goToAndPlay(0, true);
    }
  }

  function playLogoReverse() {
    if (!logoAnimRef.current) return;
    const anim = logoAnimRef.current;
    const currentFrame = anim.currentFrame ?? 0;
    if (currentFrame <= 0) return;
    anim.setSpeed(1.6);
    anim.setDirection(-1);
    anim.play();
  }

  function handleLogoClick() {
    if (!logoAnimRef.current) return;
    const anim = logoAnimRef.current;
    const totalFrames = anim.totalFrames ?? 0;
    const currentFrame = anim.currentFrame ?? 0;
    if (totalFrames && currentFrame >= totalFrames - 1) {
      anim.setSpeed(1.6);
      anim.setDirection(-1);
      anim.play();
      return;
    }
    anim.setSpeed(1.1);
    anim.setDirection(1);
    anim.goToAndPlay(0, true);
  }

  return (
    <Bar>
      <Container $isDesktop={props.isDesktop} data-testid="header">
        <MobileLeft>
          <LanguageDropdown
            currentLanguage={props.currentLanguage}
            supportedLocales={props.supportedLocales}
            setLanguage={props.setLanguage}
            testId="language-selector-mobile"
          />
        </MobileLeft>

        <LeftFrame aria-label="Header navigation">
          <LeftLink
            href={props.aboutUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header_about"
          >
            {props.aboutText}
          </LeftLink>
          <LeftLink
            href={props.registerUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header_register"
          >
            {props.registerText}
          </LeftLink>
        </LeftFrame>

        <CenterFrame>
          <Logo
            ref={logoRef}
            data-testid="brand_logo"
            aria-label="Brand logo"
            onMouseEnter={playLogoOnce}
            onMouseLeave={playLogoReverse}
            onClick={handleLogoClick}
            role="img"
          />
        </CenterFrame>

        <RightFrame>
          <LanguageDropdown
            currentLanguage={props.currentLanguage}
            supportedLocales={props.supportedLocales}
            setLanguage={props.setLanguage}
            testId="language-selector"
          />
          <SupportLink
            href={props.supportUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header_support"
          >
            {props.supportText}
          </SupportLink>
        </RightFrame>

        <MobileRight>
          <HelpIconButton
            href={props.supportUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header_help_mobile"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16H11V14H9V16ZM10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM10 4C7.79 4 6 5.79 6 8H8C8 6.9 8.9 6 10 6C11.1 6 12 6.9 12 8C12 10 9 9.75 9 13H11C11 10.75 14 10.5 14 8C14 5.79 12.21 4 10 4Z"
                fill="#23231F"
              />
            </svg>
          </HelpIconButton>
        </MobileRight>
      </Container>
    </Bar>
  );
}
